import {isFeatureEnabled} from 'feature-flags';
import {Card, Separator, DataStatus, Alert} from 'platform/components';
import {
  Box,
  Display,
  HStack,
  VStack,
  Text,
  Show,
  getCssSize,
  Clickable,
  Hide,
  useDevice,
} from 'platform/foundation';
import styled from 'styled-components';

import {Fragment} from 'react';

import {isNil} from 'ramda';

import {useGetVehicleStockOverviewQuery, VehicleStockOverviewResponseBody} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {useDataQuery, useGetCurrentBranch} from '@dms/shared';

import {
  Nullish,
  TestIdProps,
  composePath,
  parseDate,
  suffixTestId,
  useNavigate,
  isLastIndexInArray,
} from 'shared/web';

import {useRedirectFromOpportunities} from '../hooks/useRedirectFromOpportunities/useRedirectFromOpportunities';

interface VehicleWarehouseProps extends TestIdProps {
  branchId: string | undefined;
  isActiveBranch: boolean;
}

const FROM_DATE = '2000-01-01';
const DAYS_TO_EXPIRE = 30;

export function VehicleWarehouse(props: VehicleWarehouseProps) {
  const navigate = useNavigate();
  const {activeBranchId} = useGetCurrentBranch();
  const {data: user} = useGetCurrentUserInfoQuery();
  const device = useDevice();

  const [handleRedirectFromOpportunity, isRedirecting] = useRedirectFromOpportunities({
    branchId: props.branchId,
    ownerSegment: null,
    typeSegment: null,
  });

  const {
    data: vehicleStockOverview,
    isLoading: isVehicleStockOverviewLoading,
    isError: isVehicleStockOverviewError,
  } = useGetVehicleStockOverviewQuery(
    {
      branchId: props.branchId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const redirectGridCode =
    (isFeatureEnabled(featureFlags.SALES_360_VEHICLES) &&
      !isFeatureEnabled(featureFlags.REPLACE_VEHICLE_DG_WITH_OPENSEARCH)) ||
    isFeatureEnabled(featureFlags.REPLACE_VEHICLE_DG_WITH_OPENSEARCH)
      ? 'vehicle-opensearch'
      : 'vehicle';

  const branchForDataQuery = props.isActiveBranch
    ? [activeBranchId]
    : (user?.branches?.map((branch) => branch.id) ?? []);

  const {queryParams: queryParamsValidUntil} = useDataQuery(redirectGridCode, {
    // Filter vehicles with MOT expiration this month
    filters: {
      validUntil: {relativeInputKey: 'NEXT_N_DAYS', relativeInputValue: DAYS_TO_EXPIRE},
      saleState: ['for-sale', 'selling'],
      archived: ['0'],
      branch: branchForDataQuery,
    },
  });

  const yesterdaysDate = ((d) =>
    parseDate(d.setDate(d.getDate() - 1))
      .toISOString()
      .split('T')[0])(new Date());

  const {queryParams: queryParamsExpired} = useDataQuery(redirectGridCode, {
    // Filter available vehicles for sale with expired MOT
    filters: {
      validUntil: {from: FROM_DATE, to: yesterdaysDate},
      saleState: ['for-sale', 'selling'],
      archived: ['0'],
      branch: branchForDataQuery,
    },
    order: [{columnKey: 'createdAt', order: 'DESC'}],
  });

  const onWarningAlertClick = (queryParams?: string | Record<string, string | Nullish>) => {
    navigate(composePath(vehiclesRoutes.home, {queryParams}));
  };

  const columns = getColumns(vehicleStockOverview);
  const numberOfCarsWhichMotExpirating = vehicleStockOverview?.motExpirationWarning ?? 0;
  const numberOfCarsWhichMotExpired = vehicleStockOverview?.motExpiredWarning ?? 0;

  return (
    <Card title={i18n.t('page.homepage.labels.vehicleWarehouse')} hasSeparator={false}>
      <Box>
        <DataStatus
          minHeight="100%"
          isLoading={isVehicleStockOverviewLoading}
          isError={isVehicleStockOverviewError}
        >
          <VStack spacing={4}>
            <Show onMobile onTablet>
              <VStack spacing={3}>
                {columns.map((column) => (
                  <Box key={`${column.id}_${column.label}`} width="100%">
                    <HoverBox>
                      <Clickable
                        onClick={() => handleRedirectFromOpportunity(column.id)}
                        isDisabled={isRedirecting}
                      >
                        <HStack spacing={3} align="center" justify="space-between">
                          <Text color="secondary" size="xSmall">
                            {column.label}
                          </Text>
                          <Display size={2}>{String(column.value)}</Display>
                        </HStack>
                      </Clickable>
                    </HoverBox>
                  </Box>
                ))}
              </VStack>
            </Show>
            <Show onNotebook onDesktop>
              <HStack>
                {columns.map((column, index) => (
                  <Fragment key={`${column.id}_${column.label}`}>
                    <HStack basis="100%" align="stretch" spacing={4}>
                      <HoverBox>
                        <Clickable
                          onClick={() => handleRedirectFromOpportunity(column.id)}
                          isDisabled={isRedirecting}
                        >
                          <VStack spacing={4} justify="space-between" grow={1}>
                            <Text color="secondary" size="xSmall">
                              {column.label}
                            </Text>
                            <Display size={1}>{String(column.value)}</Display>
                          </VStack>
                        </Clickable>
                      </HoverBox>
                    </HStack>
                    <Hide when={isLastIndexInArray(columns, index)}>
                      <Separator spacing={4} orientation="vertical" />
                    </Hide>
                  </Fragment>
                ))}
              </HStack>
            </Show>
            <Show when={numberOfCarsWhichMotExpirating > 0}>
              <Alert
                variant="warning"
                type="inline"
                title={i18n.t('page.homepage.vehicleWarehouse.labels.motExpiresIn30Days')}
                titleSize={device === 'mobile' ? 6 : 5}
                message={i18n.t('page.homepage.vehicleWarehouse.labels.carCount', {
                  count: numberOfCarsWhichMotExpirating,
                })}
                messageSize={['xSmall', 'small']}
                hyperlinks={[
                  {
                    title: i18n.t('page.homepage.labels.view'),
                    onClick: () => onWarningAlertClick(queryParamsValidUntil),
                  },
                ]}
                data-testid={suffixTestId('motExpiresIn30DaysAlert', props)}
              />
            </Show>
            <Show when={numberOfCarsWhichMotExpired > 0}>
              <Alert
                variant="error"
                type="inline"
                title={i18n.t('page.homepage.vehicleWarehouse.labels.motExpired')}
                titleSize={device === 'mobile' ? 6 : 5}
                message={i18n.t('page.homepage.vehicleWarehouse.labels.carCount', {
                  count: numberOfCarsWhichMotExpired,
                })}
                messageSize={['xSmall', 'small']}
                hyperlinks={[
                  {
                    title: i18n.t('page.homepage.labels.view'),
                    onClick: () => onWarningAlertClick(queryParamsExpired),
                  },
                ]}
                data-testid={suffixTestId('motExpiredAlert', props)}
              />
            </Show>
          </VStack>
        </DataStatus>
      </Box>
    </Card>
  );
}

function getColumns(vehicleStockOverview: VehicleStockOverviewResponseBody | Nullish) {
  if (isNil(vehicleStockOverview)) {
    return [];
  }

  return [
    {
      label: i18n.t('page.homepage.vehicleWarehouse.labels.warehouseTotal'),
      value: vehicleStockOverview.totalForSale,
      id: 'warehouseTotal',
    },
    {
      label: i18n.t('page.homepage.vehicleWarehouse.labels.inSale'),
      value: vehicleStockOverview.selling,
      id: 'inSale',
    },
    {
      label: i18n.t('page.homepage.vehicleWarehouse.labels.inBuying'),
      value: vehicleStockOverview.buying,
      id: 'inBuying',
    },
  ] as const;
}

const HoverBox = styled.div`
  padding: ${getCssSize(2)};
  width: 100%;
  border-radius: ${({theme}) => theme.radii.medium};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][40]};
  }
`;
